import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, Copy, CheckCircle2, ChevronDown, ChevronUp,
  AlertTriangle, ExternalLink, Globe, Info, Zap, BadgeCheck, CreditCard
} from 'lucide-react';

const DNS_RECORDS = [
  {
    id: 'spf',
    type: 'TXT',
    name: '@',
    value: 'v=spf1 include:amazonses.com include:_spf.resend.com ~all',
    purpose: 'SPF (Sender Policy Framework)',
    description: 'Authorises Resend\'s mail servers to send email on behalf of mybodytoday1.com. Without this, receiving servers may reject or spam-filter your emails.',
    ttl: '3600',
  },
  {
    id: 'dkim',
    type: 'TXT',
    name: 'resend._domainkey',
    value: 'p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC...',
    purpose: 'DKIM (DomainKeys Identified Mail)',
    description: 'Cryptographically signs outgoing emails so receiving servers can verify they genuinely came from you. Required for inbox delivery with major providers.',
    ttl: '3600',
    note: 'Get your exact DKIM value from Resend Dashboard → Domains → mybodytoday1.com',
    isDynamic: true,
  },
  {
    id: 'dmarc',
    type: 'TXT',
    name: '_dmarc',
    value: 'v=DMARC1; p=none; rua=mailto:admin@mybodytoday1.com',
    purpose: 'DMARC Policy',
    description: 'Tells receiving servers what to do when SPF/DKIM checks fail. Start with p=none (monitor only) then tighten to p=quarantine or p=reject once verified.',
    ttl: '3600',
  },
];

const REGISTRARS = [
  {
    id: 'godaddy',
    name: 'GoDaddy',
    color: 'bg-green-50 border-green-200',
    headerColor: 'bg-green-600',
    steps: [
      'Log in to GoDaddy and go to My Products → Domains',
      'Click the domain mybodytoday1.com → Manage DNS',
      'Click Add New Record',
      'Select the record Type (TXT), enter the Name and Value from above',
      'Set TTL to 1 Hour (3600 seconds)',
      'Click Save and repeat for each record',
    ],
    link: 'https://dcc.godaddy.com/manage/dns',
    linkLabel: 'Open GoDaddy DNS Manager',
  },
  {
    id: 'namecheap',
    name: 'Namecheap',
    color: 'bg-orange-50 border-orange-200',
    headerColor: 'bg-orange-500',
    steps: [
      'Log in to Namecheap and go to Domain List',
      'Click Manage next to mybodytoday1.com',
      'Select the Advanced DNS tab',
      'Click Add New Record',
      'Choose TXT Record, enter the Host (Name) and Value',
      'Set TTL to Automatic (or 3600)',
      'Click the green checkmark to save, repeat for each record',
    ],
    link: 'https://ap.www.namecheap.com/domains/list/',
    linkLabel: 'Open Namecheap Domain List',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    color: 'bg-amber-50 border-amber-200',
    headerColor: 'bg-amber-500',
    steps: [
      'Log in to Cloudflare and select mybodytoday1.com',
      'Go to DNS → Records',
      'Click Add record',
      'Set Type to TXT, enter the Name and Content (Value)',
      'Set Proxy status to DNS only (grey cloud — NOT proxied)',
      'Set TTL to Auto',
      'Click Save and repeat for each record',
    ],
    link: 'https://dash.cloudflare.com/',
    linkLabel: 'Open Cloudflare Dashboard',
  },
  {
    id: 'route53',
    name: 'AWS Route 53',
    color: 'bg-yellow-50 border-yellow-200',
    headerColor: 'bg-yellow-600',
    steps: [
      'Log in to AWS Console and open Route 53',
      'Click Hosted zones → mybodytoday1.com',
      'Click Create record',
      'Set Record type to TXT',
      'Enter the Record name (subdomain part only, e.g. resend._domainkey)',
      'Paste the Value in the Value field (wrap in double quotes if required)',
      'Set TTL to 300 or 3600',
      'Click Create records and repeat for each record',
    ],
    link: 'https://console.aws.amazon.com/route53/',
    linkLabel: 'Open AWS Route 53',
  },
];

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      const el = document.createElement('textarea');
      el.value = value;
      document.body?.appendChild(el);
      el?.select();
      document.execCommand('copy');
      document.body?.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
        copied
          ? 'bg-green-50 border-green-300 text-green-700' :'bg-white border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-700'
      }`}
      title={`Copy ${label}`}
    >
      {copied ? (
        <>
          <CheckCircle2 className="w-3.5 h-3.5" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" />
          Copy
        </>
      )}
    </button>
  );
};

const RecordRow = ({ label, value, isDynamic }) => (
  <div className="space-y-1.5">
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    <div className="flex items-start gap-2">
      <div className={`flex-1 font-mono text-xs rounded-lg px-3 py-2 break-all leading-relaxed ${
        isDynamic ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-gray-50 border border-gray-200 text-gray-800'
      }`}>
        {value}
      </div>
      {!isDynamic && <CopyButton value={value} label={label} />}
    </div>
  </div>
);

const RegistrarCard = ({ registrar }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`border rounded-xl overflow-hidden ${registrar?.color}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:opacity-90 transition-opacity"
      >
        <div className={`w-2 h-2 rounded-full ${registrar?.headerColor}`} />
        <span className="text-sm font-semibold text-gray-800 flex-1 text-left">{registrar?.name}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-current border-opacity-20">
          <ol className="space-y-2 mt-3">
            {registrar?.steps?.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700">
                <span className={`flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5 ${registrar?.headerColor}`}>
                  {i + 1}
                </span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          <a
            href={registrar?.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-900 underline underline-offset-2 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {registrar?.linkLabel}
          </a>
        </div>
      )}
    </div>
  );
};

const DnsSetup = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>DNS Setup — SPF/DKIM for mybodytoday1.com | MyBodyToday</title>
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-teal-100 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Email DNS Setup</h1>
              <p className="text-xs text-gray-500">SPF, DKIM & DMARC for mybodytoday1.com</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Domain Verified Banner */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex gap-4">
          <BadgeCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-green-900 font-semibold text-sm">mybodytoday1.com is already verified in Resend</p>
            <p className="text-green-800 text-sm leading-relaxed">
              Your domain is verified and the SPF/DKIM DNS records are correctly configured. You do <strong>not</strong> need to add or re-verify this domain in Resend.
            </p>
          </div>
        </div>

        {/* 403 / Free Plan Restriction */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-amber-900 font-semibold text-sm">403 sending error — Resend free plan limit reached</p>
            <p className="text-amber-800 text-sm leading-relaxed">
              The 403 error you're seeing is <strong>not</strong> caused by missing DNS records. It is caused by the <strong>Resend free plan</strong>, which restricts sending to only verified email addresses (your own Resend account email). To send campaigns to all recipients, you need to upgrade your Resend plan.
            </p>
            <a
              href="https://resend.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-1 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              Upgrade Resend Plan
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* What upgrading unlocks */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-4 flex items-center gap-3">
            <div className="bg-white/20 rounded-lg p-1.5">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-sm">What upgrading your Resend plan unlocks</h2>
              <p className="text-teal-100 text-xs mt-0.5">Remove the free plan sending restriction</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <ul className="space-y-2">
              {[
                'Send emails to any recipient — not just your own verified Resend address',
                'Remove the 403 "Forbidden" error when sending re-engagement campaigns',
                'Higher monthly sending volume (3,000+ emails/month on paid plans)',
                'Access to dedicated sending infrastructure for better deliverability',
                'Your domain mybodytoday1.com is already verified — no DNS changes needed after upgrading',
              ]?.map((item, i) => (
                <li key={i} className="flex gap-3 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://resend.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-900 underline underline-offset-2 transition-colors mt-2"
            >
              <ExternalLink className="w-4 h-4" />
              View Resend Pricing Plans
            </a>
          </div>
        </div>

        {/* DNS Records Reference */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-4 flex items-center gap-3">
            <div className="bg-white/20 rounded-lg p-1.5">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-sm">DNS Records Reference (already applied)</h2>
              <p className="text-violet-100 text-xs mt-0.5">These records are verified on mybodytoday1.com — for reference only</p>
            </div>
          </div>
          <div className="p-5 space-y-5">
            {DNS_RECORDS?.map((record) => (
              <div key={record?.id} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                  <span className="bg-violet-100 text-violet-700 text-xs font-bold px-2 py-0.5 rounded font-mono">
                    {record?.type}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">{record?.purpose}</span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-green-700 font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-gray-600 leading-relaxed">{record?.description}</p>

                  {record?.isDynamic && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-amber-800 text-xs">{record?.note}</p>
                    </div>
                  )}

                  <div className="grid gap-3">
                    <RecordRow label="Type" value={record?.type} isDynamic={false} />
                    <RecordRow label="Name / Host" value={record?.name} isDynamic={false} />
                    <RecordRow label="Value / Content" value={record?.value} isDynamic={record?.isDynamic} />
                    <RecordRow label="TTL" value={record?.ttl} isDynamic={false} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Registrar Instructions (collapsed reference) */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-gray-700 to-gray-900 px-5 py-4 flex items-center gap-3">
            <div className="bg-white/20 rounded-lg p-1.5">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-sm">Registrar-Specific Instructions (Reference)</h2>
              <p className="text-gray-300 text-xs mt-0.5">DNS is already verified — this is for future reference only</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {REGISTRARS?.map((registrar) => (
              <RegistrarCard key={registrar?.id} registrar={registrar} />
            ))}
            <p className="text-xs text-gray-500 pt-1">
              Using a different registrar? Search for "add TXT record" in your registrar's help centre — the process is similar across all providers.
            </p>
          </div>
        </div>

        {/* Webhook setup note */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex gap-4">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-blue-900 font-semibold text-sm">Resend Delivery Webhooks</p>
            <p className="text-blue-800 text-sm leading-relaxed">
              A <strong>resend-webhook</strong> edge function has been deployed to your Supabase project. Register it in your Resend Dashboard under <strong>Webhooks</strong> to receive real-time delivery confirmations, bounces, and complaints — replacing API-acceptance status with actual inbox confirmation.
            </p>
            <p className="text-blue-700 text-xs font-mono bg-blue-100 px-2 py-1 rounded mt-2 break-all">
              {`${import.meta.env?.VITE_SUPABASE_URL || 'https://[project].supabase.co'}/functions/v1/resend-webhook`}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DnsSetup;
